import { useEffect, useState } from "react";
import { Eye, Users as UsersIcon } from "lucide-react";
import api from "@/lib/api";

/**
 * VisitsChart — 30-day site visits chart.
 * Renders an inline SVG bar chart (no library needed).
 * Compact desktop + mobile.
 */
export default function VisitsChart() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/site-stats");
        setStats(data);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  if (!stats) {
    return <div className="sl-card text-center text-gray-500 text-sm">Chargement des stats de visites…</div>;
  }

  const series = stats.series_30d || [];
  const maxV = Math.max(1, ...series.map((d) => d.views));

  const fmt = (d) => {
    const dt = new Date(d + "T00:00:00Z");
    return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  };

  return (
    <div className="sl-card" data-testid="visits-chart-card">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Visites du site (30 derniers jours)</div>
          <div className="mt-1 text-xs text-gray-500">
            Aujourd&apos;hui : <span className="font-semibold text-gray-800">{stats.today.views}</span> vues ·
            <span className="font-semibold text-gray-800"> {stats.today.unique}</span> uniques
          </div>
        </div>
        <div className="flex gap-2">
          <Tile icon={Eye} label="Vues" value={stats.totals_30d.views} hint="30j" />
          <Tile icon={UsersIcon} label="Uniques" value={stats.totals_30d.unique} hint="30j" />
          <Tile icon={Eye} label="Total" value={stats.totals_all.views} hint="depuis l'origine" highlight />
        </div>
      </div>

      {/* Inline SVG bar chart */}
      <div className="relative w-full overflow-x-auto" data-testid="visits-chart-svg">
        <svg viewBox={`0 0 ${Math.max(640, series.length * 22)} 180`} className="w-full h-[180px] min-w-full">
          {series.map((d, i) => {
            const x = 8 + i * 22;
            const h = (d.views / maxV) * 130;
            const y = 150 - h;
            const uh = (d.unique / maxV) * 130;
            const uy = 150 - uh;
            return (
              <g key={d.date}>
                {/* Views bar (background) */}
                <rect x={x} y={y} width={16} height={h} rx={3} fill="var(--sl-primary)" opacity={0.25} />
                {/* Unique bar (foreground, narrower) */}
                <rect x={x + 4} y={uy} width={8} height={uh} rx={2} fill="var(--sl-primary)" />
                {/* X label every 5 days */}
                {i % 5 === 0 && (
                  <text x={x + 8} y={170} fontSize={10} fill="#999" textAnchor="middle">{fmt(d.date)}</text>
                )}
              </g>
            );
          })}
          {/* Axis baseline */}
          <line x1="0" y1="151" x2="9999" y2="151" stroke="#eee" strokeWidth="1" />
        </svg>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[var(--sl-primary)] opacity-30"></span> Pages vues</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[var(--sl-primary)]"></span> Visiteurs uniques</span>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, hint, highlight }) {
  return (
    <div className={`rounded-xl px-3 py-2 text-right ${highlight ? "bg-[var(--sl-primary)]/10 text-[var(--sl-primary)]" : "bg-[var(--sl-bg-alt)] text-gray-700"}`}>
      <div className="flex items-center justify-end gap-1 text-[10px] uppercase tracking-widest font-semibold">
        <Icon size={10} /> {label}
      </div>
      <div className="font-heading text-base sm:text-lg font-bold mt-0.5">{value.toLocaleString("fr-FR")}</div>
      <div className="text-[9px] text-gray-400">{hint}</div>
    </div>
  );
}
